import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Card,
  Button,
  Row,
  Col,
  Form,
  Pagination,
  Badge,
} from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { FaSearch, FaPlus, FaTimes, FaCheckCircle } from 'react-icons/fa';
import moment from 'moment';

import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import Tabela from '~/Components/TabelaDinamica/Tabela';
import { selectStylesSm } from '~/Styles/reactSelectStyles';
import { extrairMensagemErro } from '~/Utils/Helpers';
import {
  listarTodosGruposEmpresa,
  monitoraListar,
  monitoraClientes,
} from '~/Services/Service';

import ModalNovoMonitoramento from './ModalNovoMonitoramento';
import ModalFinalizarProcesso from './ModalFinalizarProcesso';

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: 'A', label: 'Ativos' },
  { value: 'I', label: 'Inativos' },
  { value: 'T', label: 'Todos' },
];

const formatarDataHora = (v) => {
  if (!v) return '-';
  const m = moment(v);
  return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : '-';
};

const formatarData = (v) => {
  if (!v) return '-';
  const m = moment(v);
  return m.isValid() ? m.format('DD/MM/YYYY') : '-';
};

const renderStatusBadge = (value) => {
  if (value === 'A') return <Badge variant="success">Ativo</Badge>;
  if (value === 'I') return <Badge variant="secondary">Inativo</Badge>;
  return value || '-';
};

const Monitora = () => {
  const [geOptions, setGeOptions] = useState([]);
  const [selectedGe, setSelectedGe] = useState(null);
  const [loadingGes, setLoadingGes] = useState(false);

  const [clientesOptions, setClientesOptions] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [loadingClientes, setLoadingClientes] = useState(false);

  const [statusFiltro, setStatusFiltro] = useState('A');

  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [ordenacao, setOrdenacao] = useState({ coluna: 'inicio', direcao: 'desc' });
  const [loadingLista, setLoadingLista] = useState(false);
  const [buscaFeita, setBuscaFeita] = useState(false);

  const [linhaSelecionada, setLinhaSelecionada] = useState(null);

  const [modalNovoOpen, setModalNovoOpen] = useState(false);
  const [modalFinalizarOpen, setModalFinalizarOpen] = useState(false);

  // Carregar GEs do usuário
  useEffect(() => {
    let ativo = true;
    setLoadingGes(true);
    listarTodosGruposEmpresa()
      .then((res) => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : [];
        const opts = lista.map((g) => ({
          value: g.codigo_grupo_empresa,
          label: `${g.codigo_grupo_empresa} - ${g.apelido || g.descricao || ''}`.trim(),
        }));
        setGeOptions(opts);
        if (opts.length === 1) {
          setSelectedGe(opts[0]);
        }
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
        setGeOptions([]);
      })
      .finally(() => {
        if (ativo) setLoadingGes(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  // Carregar clientes quando GE mudar
  useEffect(() => {
    if (!selectedGe) {
      setClientesOptions([]);
      setSelectedCliente(null);
      return undefined;
    }
    let ativo = true;
    setLoadingClientes(true);
    monitoraClientes(selectedGe.value)
      .then((res) => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : [];
        const opts = lista.map((c) => ({
          value: c.codigo_conta,
          label: `${c.codigo_conta} - ${c.apelido_conta || ''}`.trim(),
          raw: c,
        }));
        setClientesOptions(opts);
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
        setClientesOptions([]);
      })
      .finally(() => {
        if (ativo) setLoadingClientes(false);
      });
    return () => {
      ativo = false;
    };
  }, [selectedGe]);

  const colunaParaSortKey = useCallback((colunaDado) => {
    // Inline lookup pra não criar dep cíclica com `colunas`. Snake → camelCase.
    const mapa = {
      boleto: 'boleto',
      status: 'status',
      codigo_grupo_empresa: 'codigoGrupoEmpresa',
      cliente: 'cliente',
      apelido_conta: 'apelidoConta',
      nome_da_familia: 'nomeDaFamilia',
      mapa1: 'mapa1',
      mapa2: 'mapa2',
      inicio: 'inicio',
      ate: 'ate',
      nome_i: 'nomeI',
      processo_i: 'processoI',
      nome_f: 'nomeF',
      processo_f: 'processoF',
    };
    return mapa[colunaDado] || 'inicio';
  }, []);

  const buscar = useCallback(
    (paginaAlvo, ordenacaoAlvo) => {
      if (!selectedGe) {
        toast.warn('Selecione um Grupo de Empresas para buscar.');
        return;
      }
      const params = {
        ge: selectedGe.value,
        status: statusFiltro,
        page: paginaAlvo,
        pageSize: PAGE_SIZE,
        sortColumn: colunaParaSortKey(ordenacaoAlvo.coluna),
        sortDir: ordenacaoAlvo.direcao,
      };
      if (selectedCliente) {
        params.cliente = selectedCliente.value;
      }
      setLoadingLista(true);
      setBuscaFeita(true);
      monitoraListar(params)
        .then((res) => {
          const items = res && Array.isArray(res.items) ? res.items : [];
          setItens(items);
          setTotal((res && res.total) || 0);
          setPage((res && res.page) || paginaAlvo);
          setLinhaSelecionada(null);
        })
        .catch((err) => {
          toast.error(extrairMensagemErro(err));
          setItens([]);
          setTotal(0);
        })
        .finally(() => {
          setLoadingLista(false);
        });
    },
    [selectedGe, selectedCliente, statusFiltro, colunaParaSortKey]
  );

  const onClickBuscar = () => {
    buscar(1, ordenacao);
  };

  const onCancelar = () => {
    setSelectedCliente(null);
    setStatusFiltro('A');
    setOrdenacao({ coluna: 'inicio', direcao: 'desc' });
    setItens([]);
    setTotal(0);
    setPage(1);
    setBuscaFeita(false);
    setLinhaSelecionada(null);
  };

  const onOrdenacaoChange = (nova) => {
    setOrdenacao(nova);
    if (buscaFeita) {
      buscar(1, nova);
    }
  };

  const onPageChange = (novaPagina) => {
    if (novaPagina === page || novaPagina < 1) return;
    buscar(novaPagina, ordenacao);
  };

  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Mapeamento campo de dados (snake_case) → coluna de sort aceita pela whitelist
  // do backend (camelCase, ver TechSpec §5). API serializa com snake_case mas o
  // contrato de sortColumn é PascalCase/camelCase do DTO C#.
  const colunas = useMemo(
    () => [
      {
        key: 'boleto',
        title: 'Bol.',
        ordenacao: true,
        width: '80px',
        sortKey: 'boleto',
      },
      {
        key: 'status',
        title: 'Status',
        ordenacao: true,
        width: '90px',
        sortKey: 'status',
        render: renderStatusBadge,
      },
      {
        key: 'codigo_grupo_empresa',
        title: 'GE',
        ordenacao: true,
        width: '70px',
        sortKey: 'codigoGrupoEmpresa',
      },
      {
        key: 'cliente',
        title: 'Cliente',
        ordenacao: true,
        width: '90px',
        sortKey: 'cliente',
      },
      {
        key: 'apelido_conta',
        title: 'Nome',
        ordenacao: true,
        sortKey: 'apelidoConta',
      },
      {
        key: 'nome_da_familia',
        title: 'Empreendimento',
        ordenacao: true,
        sortKey: 'nomeDaFamilia',
      },
      {
        key: 'mapa1',
        title: 'QD',
        ordenacao: true,
        width: '70px',
        sortKey: 'mapa1',
      },
      {
        key: 'mapa2',
        title: 'LT',
        ordenacao: true,
        width: '70px',
        sortKey: 'mapa2',
      },
      {
        key: 'inicio',
        title: 'Início',
        ordenacao: true,
        width: '140px',
        sortKey: 'inicio',
        render: formatarDataHora,
      },
      {
        key: 'ate',
        title: 'Monitorar até',
        ordenacao: true,
        width: '120px',
        sortKey: 'ate',
        render: formatarData,
      },
      {
        key: 'nome_i',
        title: 'Iniciado por',
        ordenacao: true,
        sortKey: 'nomeI',
      },
      {
        key: 'processo_i',
        title: 'Processo I',
        ordenacao: true,
        width: '110px',
        sortKey: 'processoI',
      },
      {
        key: 'nome_f',
        title: 'Finalizado por',
        ordenacao: true,
        sortKey: 'nomeF',
      },
      {
        key: 'processo_f',
        title: 'Processo F',
        ordenacao: true,
        width: '110px',
        sortKey: 'processoF',
      },
    ],
    []
  );

  // `id` composto é consumido como key estável pelo TabelaDinamica (FE-001).
  const dadosTabela = useMemo(
    () =>
      itens.map((it) => ({
        ...it,
        id: `${it.boleto}-${it.status}-${it.inicio || ''}`,
      })),
    [itens]
  );

  const handleClickLinha = (item) => {
    setLinhaSelecionada(item);
  };

  const isLinhaSelecionada = (item) => {
    if (!linhaSelecionada) return false;
    return (
      linhaSelecionada.boleto === item.boleto &&
      linhaSelecionada.status === item.status &&
      linhaSelecionada.inicio === item.inicio
    );
  };

  const podeFinalizar =
    !!linhaSelecionada && linhaSelecionada.status === 'A';

  const podeNovoMonitoramento = !!selectedGe && !!selectedCliente;

  const renderPaginacao = () => {
    if (!buscaFeita || total === 0 || totalPaginas <= 1) return null;
    const items = [];
    const maxBotoes = 5;
    let inicio = Math.max(1, page - Math.floor(maxBotoes / 2));
    let fim = Math.min(totalPaginas, inicio + maxBotoes - 1);
    if (fim - inicio + 1 < maxBotoes) {
      inicio = Math.max(1, fim - maxBotoes + 1);
    }
    items.push(
      <Pagination.First
        key="first"
        disabled={page === 1}
        onClick={() => onPageChange(1)}
      />
    );
    items.push(
      <Pagination.Prev
        key="prev"
        disabled={page === 1}
        onClick={() => onPageChange(page - 1)}
      />
    );
    if (inicio > 1) {
      items.push(<Pagination.Ellipsis key="ellipsis-inicio" disabled />);
    }
    for (let n = inicio; n <= fim; n += 1) {
      items.push(
        <Pagination.Item
          key={`p-${n}`}
          active={n === page}
          onClick={() => onPageChange(n)}
        >
          {n}
        </Pagination.Item>
      );
    }
    if (fim < totalPaginas) {
      items.push(<Pagination.Ellipsis key="ellipsis-fim" disabled />);
    }
    items.push(
      <Pagination.Next
        key="next"
        disabled={page === totalPaginas}
        onClick={() => onPageChange(page + 1)}
      />
    );
    items.push(
      <Pagination.Last
        key="last"
        disabled={page === totalPaginas}
        onClick={() => onPageChange(totalPaginas)}
      />
    );
    const inicioRange = (page - 1) * PAGE_SIZE + 1;
    const fimRange = Math.min(page * PAGE_SIZE, total);
    return (
      <div className="d-flex flex-wrap align-items-center justify-content-between mt-3">
        <small className="text-muted mb-2">
          Mostrando {inicioRange} até {fimRange} de {total} registros
        </small>
        <Pagination size="sm" className="mb-0">
          {items}
        </Pagination>
      </div>
    );
  };

  return (
    <Container fluid className="py-3">
      <Header title="Monitoramento de Clientes" />

      <Card className="mb-3">
        <Card.Body>
          <Row className="align-items-end">
            <Col md={4} sm={12} className="mb-2">
              <Form.Label className="small mb-1">Grupo de Empresas</Form.Label>
              <Select
                styles={selectStylesSm}
                placeholder="Selecione..."
                options={geOptions}
                value={selectedGe}
                onChange={(opt) => {
                  setSelectedGe(opt);
                  setSelectedCliente(null);
                }}
                isLoading={loadingGes}
                isClearable
                noOptionsMessage={() => 'Nenhum GE disponível'}
              />
            </Col>
            <Col md={4} sm={12} className="mb-2">
              <Form.Label className="small mb-1">Cliente</Form.Label>
              <Select
                styles={selectStylesSm}
                placeholder="Todos do GE"
                options={clientesOptions}
                value={selectedCliente}
                onChange={setSelectedCliente}
                isLoading={loadingClientes}
                isDisabled={!selectedGe}
                isClearable
                noOptionsMessage={() => 'Nenhum cliente'}
              />
            </Col>
            <Col md={4} sm={12} className="mb-2">
              <Form.Label className="small mb-1 d-block">Status</Form.Label>
              <div className="d-flex flex-wrap">
                {STATUS_OPTIONS.map((opt) => (
                  <Form.Check
                    key={opt.value}
                    inline
                    type="radio"
                    id={`status-${opt.value}`}
                    name="statusFiltro"
                    label={opt.label}
                    checked={statusFiltro === opt.value}
                    onChange={() => setStatusFiltro(opt.value)}
                  />
                ))}
              </div>
            </Col>
          </Row>
          <Row>
            <Col className="d-flex flex-wrap">
              <Button
                variant="primary"
                size="sm"
                className="mr-2 mb-2"
                onClick={onClickBuscar}
                disabled={loadingLista || !selectedGe}
              >
                <FaSearch className="mr-1" /> Buscar
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="mr-2 mb-2"
                onClick={onCancelar}
                disabled={loadingLista}
              >
                <FaTimes className="mr-1" /> Cancelar
              </Button>
              <Button
                variant="success"
                size="sm"
                className="mr-2 mb-2"
                onClick={() => setModalNovoOpen(true)}
                disabled={!podeNovoMonitoramento}
                title={
                  podeNovoMonitoramento
                    ? 'Abrir cadastro de monitoramento'
                    : 'Selecione GE e Cliente para habilitar'
                }
              >
                <FaPlus className="mr-1" /> Novo Monitoramento
              </Button>
              <Button
                variant="warning"
                size="sm"
                className="mb-2"
                onClick={() => setModalFinalizarOpen(true)}
                disabled={!podeFinalizar}
                title={
                  podeFinalizar
                    ? 'Finalizar o monitoramento selecionado'
                    : 'Selecione uma linha com status Ativo'
                }
              >
                <FaCheckCircle className="mr-1" /> Finalizar Processo
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loadingLista && <Loading />}

      {!loadingLista && buscaFeita && (
        <Card>
          <Card.Body>
            <Tabela
              colunas={colunas}
              dados={dadosTabela}
              fontSize="13px"
              cliqueLinha={handleClickLinha}
              linhaSelecionada={isLinhaSelecionada}
              ordenacao={ordenacao}
              onOrdenacaoChange={onOrdenacaoChange}
            />
            {renderPaginacao()}
          </Card.Body>
        </Card>
      )}

      {modalNovoOpen && (
        <ModalNovoMonitoramento
          show={modalNovoOpen}
          ge={selectedGe ? selectedGe.value : null}
          cliente={selectedCliente ? selectedCliente.value : null}
          clienteLabel={selectedCliente ? selectedCliente.label : ''}
          onHide={() => setModalNovoOpen(false)}
          onSucesso={() => {
            setModalNovoOpen(false);
            // UX-001: novos itens chegam no topo (sort inicio desc) — voltar pra page 1.
            buscar(1, ordenacao);
          }}
        />
      )}

      {modalFinalizarOpen && linhaSelecionada && (
        <ModalFinalizarProcesso
          show={modalFinalizarOpen}
          boleto={linhaSelecionada.boleto}
          mapa1={linhaSelecionada.mapa1}
          mapa2={linhaSelecionada.mapa2}
          onHide={() => setModalFinalizarOpen(false)}
          onSucesso={() => {
            setModalFinalizarOpen(false);
            // UX-001: ao inativar item, o filtro "Ativos" pode esvaziar a página atual.
            // Refazer a busca a partir da página 1 evita renderizar lista vazia.
            buscar(1, ordenacao);
          }}
        />
      )}
    </Container>
  );
};

export default Monitora;
